import React, { useCallback, useContext, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Save, RefreshCw, Plus, Users, Image } from 'lucide-react';
import { ThemeContext } from '../../constant/theme';

import { useCompanies } from './hooks/useCompanies';
import { usePermissions } from './hooks/usePermissions';
import { useSplash } from './hooks/useSplash';
import { useAssignAdmin } from './hooks/useAssignAdmin';
import { getStyles } from './styles/configurationStyles';

import CompanyList from './components/CompanyList';
import ModulePermissions from './components/ModulePermissions';
import CompanyStatusToggle from './components/CompanyStatusToggle';
import SplashSection from './components/SplashSection';
import SubscriptionDetails from './components/SubscriptionDetails';
import AddCompanyModal from './components/AddCompanyModal';
import AssignAdminModal from './components/AssignAdminModal';
import MessageAlert from './components/MessageAlert';
import { SkeletonStyles, SkeletonBox, LoadingPill } from '../../components/SkeletonLoader';

const ConfigurationManagement = () => {
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const splashSectionRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const showMessage = useCallback((type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  }, []);

  const { companies, loading: loadingCompanies, fetchCompanies, addCompany } = useCompanies(showMessage);

  const {
    permissions,
    selectedCompany,
    loading: loadingPermissions,
    saving,
    fetchCompanyPermissions,
    handleSave,
    handleToggle,
    handlePermissionChange,
  } = usePermissions(showMessage, fetchCompanies);

  const {
    splashInfo,
    loadingSplash,
    uploadingSplash,
    splashUrlInput,
    setSplashUrlInput,
    splashFile,
    splashPreviewUrl,
    splashActive,
    iconUrlInput,
    setIconUrlInput,
    iconFile,
    iconPreviewUrl,
    setIconPreviewUrl,
    fetchSplashData,
    handleSplashFileChange,
    clearSplashFile,
    handleIconFileChange,
    clearIconFile,
    handleSplashSave,
    handleSplashDeactivate,
    toggleSplashActive,
  } = useSplash(showMessage);

  const {
    showAssignAdminModal,
    adminCandidates,
    selectedAdminIds,
    adminSearch,
    setAdminSearch,
    loadingAdmins,
    openAssignAdmin,
    handleToggleAdmin,
    handleSaveAdmins,
    closeAssignAdminModal,
  } = useAssignAdmin(showMessage);

  const styles = getStyles(theme);
  const loading = loadingCompanies || loadingPermissions;

  // ── Initial-load skeleton — only until the company list first arrives ────────
  const [pageReady, setPageReady] = useState(false);
  React.useEffect(() => {
    if (!loadingCompanies) setPageReady(true);
  }, [loadingCompanies]);

  const handleCompanySelect = (clientName) => {
    fetchCompanyPermissions(clientName, (name) => fetchSplashData(name));
  };

  // ═══════════════════════════════════════════════════════════
  // INITIAL SKELETON — only until the company list first loads
  // ═══════════════════════════════════════════════════════════
  if (!pageReady && loadingCompanies) {
    return (
      <div style={styles.container}>
        <SkeletonStyles theme={theme} />
        <LoadingPill text="Loading company configuration..." theme={theme} isRTL={false} />

        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px', paddingBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <SkeletonBox width="44px" height="44px" style={{ borderRadius: '12px' }} />
              <div>
                <SkeletonBox width="200px" height="22px" style={{ marginBottom: '8px' }} />
                <SkeletonBox width="260px" height="14px" />
              </div>
            </div>
            <SkeletonBox width="140px" height="38px" style={{ borderRadius: '10px' }} />
          </div>

          <div className="responsive-two-col" style={styles.gridContainer}>
            <div style={styles.companyList}>
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonBox key={i} width="100%" height="64px" style={{ borderRadius: '12px', marginBottom: '8px' }} />
              ))}
            </div>
            <div style={styles.permissionsContainer}>
              <SkeletonBox width="220px" height="24px" style={{ marginBottom: '20px' }} />
              {[1, 2, 3, 4].map((i) => (
                <SkeletonBox key={i} width="100%" height="46px" style={{ borderRadius: '10px', marginBottom: '10px' }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {showAddModal && (
        <AddCompanyModal
          theme={theme}
          styles={styles}
          onAdd={addCompany}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showAssignAdminModal && (
        <AssignAdminModal
          theme={theme}
          styles={styles}
          selectedCompany={selectedCompany}
          adminCandidates={adminCandidates}
          selectedAdminIds={selectedAdminIds}
          adminSearch={adminSearch}
          loadingAdmins={loadingAdmins}
          setAdminSearch={setAdminSearch}
          onToggleAdmin={handleToggleAdmin}
          onSave={() => handleSaveAdmins(selectedCompany)}
          onClose={closeAssignAdminModal}
        />
      )}

      <div style={styles.card}>
        {/* Page Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          paddingBottom: '20px',
          borderBottom: theme === 'light' ? '1px solid #f0f4f8' : '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(46,124,186,0.15), rgba(46,124,186,0.08))',
              border: '1px solid rgba(46,124,186,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#2e7cba',
            }}>
              <Settings size={22} />
            </div>
            <div>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '700',
                color: theme === 'light' ? '#0f172a' : '#ffffff',
                margin: 0,
                letterSpacing: '-0.3px',
              }}>
                Company Configuration
              </h2>
              <p style={{
                fontSize: '13px',
                color: theme === 'light' ? '#64748b' : 'rgba(255,255,255,0.5)',
                margin: '2px 0 0 0',
              }}>
                Manage modules, permissions & splash screens
              </p>
            </div>
          </div>
          <button
            style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 18px',
              fontSize: '13.5px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '7px',
              boxShadow: '0 4px 12px rgba(16,185,129,0.3)',
            }}
            onClick={() => setShowAddModal(true)}
          >
            <Plus size={16} />
            Add Company
          </button>
        </div>

        <MessageAlert message={message} styles={styles} />

        {loading && !permissions ? (
          <div style={styles.emptyState}>
            <RefreshCw size={64} style={{ marginBottom: '20px', opacity: 0.3, animation: 'spin 2s linear infinite' }} />
            <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>Loading...</h3>
          </div>
        ) : (
          <div className="responsive-two-col" style={styles.gridContainer}>
            {/* Company List */}
            <CompanyList
              companies={companies}
              selectedCompany={selectedCompany}
              theme={theme}
              styles={styles}
              onSelect={handleCompanySelect}
            />

            {/* Permissions Panel */}
            {!permissions ? (
              <div style={styles.emptyState}>
                <Settings size={64} style={{ marginBottom: '20px', opacity: 0.3 }} />
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  marginBottom: '8px',
                  color: theme === 'light' ? '#374151' : 'rgba(255, 255, 255, 0.8)',
                }}>
                  Select a Company
                </h3>
                <p>Choose a company from the list to configure modules</p>
              </div>
            ) : (
              <div style={styles.permissionsContainer}>
                {/* Company Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '20px',
                  paddingBottom: '16px',
                  borderBottom: theme === 'light' ? '1px solid #f0f4f8' : '1px solid rgba(255,255,255,0.06)',
                }}>
                  <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '11px',
                    background: 'linear-gradient(135deg, #2e7cba, #1e5f8f)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: '800',
                    flexShrink: 0,
                  }}>
                    {selectedCompany?.slice(0, 2).toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: '17px',
                      fontWeight: '700',
                      color: theme === 'light' ? '#0f172a' : '#ffffff',
                      margin: 0,
                      letterSpacing: '-0.2px',
                    }}>
                      {selectedCompany}
                    </h3>
                    <p style={{
                      fontSize: '12.5px',
                      color: theme === 'light' ? '#64748b' : 'rgba(255,255,255,0.5)',
                      margin: '2px 0 0 0',
                    }}>
                      Configure modules & permissions
                    </p>
                  </div>
                  <button
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '9px 18px',
                      fontSize: '13.5px', fontWeight: '600',
                      borderRadius: '9px',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'linear-gradient(135deg, #2e7cba, #1e5f8f)',
                      color: '#ffffff',
                      boxShadow: 'none',
                      opacity: saving ? 0.75 : 1,
                      transition: 'opacity 0.15s',
                    }}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving
                      ? <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Save size={14} />}
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  marginBottom: '24px',
                }}>
                  {[
                    { label: 'Create Admin', icon: <Users size={14} />, onClick: () => navigate('/DashboardSuper/AdminAccountCreation') },
                    { label: 'Assign Admin', icon: <Users size={14} />, onClick: () => openAssignAdmin(selectedCompany) },
                    { label: 'Splash Screen', icon: <Image size={14} />, onClick: () => splashSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) },
                  ].map(btn => (
                    <button
                      key={btn.label}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '8px 14px',
                        fontSize: '13px', fontWeight: '600',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        border: theme === 'light' ? '1.5px solid #e2e8f0' : '1.5px solid rgba(255,255,255,0.1)',
                        background: theme === 'light' ? '#ffffff' : 'rgba(255,255,255,0.05)',
                        color: theme === 'light' ? '#374151' : 'rgba(255,255,255,0.8)',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#2e7cba';
                        e.currentTarget.style.color = '#2e7cba';
                        e.currentTarget.style.background = theme === 'light' ? 'rgba(46,124,186,0.05)' : 'rgba(46,124,186,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = theme === 'light' ? '#e2e8f0' : 'rgba(255,255,255,0.1)';
                        e.currentTarget.style.color = theme === 'light' ? '#374151' : 'rgba(255,255,255,0.8)';
                        e.currentTarget.style.background = theme === 'light' ? '#ffffff' : 'rgba(255,255,255,0.05)';
                      }}
                      onClick={btn.onClick}
                    >
                      {btn.icon}
                      {btn.label}
                    </button>
                  ))}
                </div>

                {/* Splash Section */}
                <SplashSection
                  theme={theme}
                  styles={styles}
                  selectedCompany={selectedCompany}
                  splashInfo={splashInfo}
                  loadingSplash={loadingSplash}
                  uploadingSplash={uploadingSplash}
                  splashPreviewUrl={splashPreviewUrl}
                  splashUrlInput={splashUrlInput}
                  setSplashUrlInput={setSplashUrlInput}
                  splashActive={splashActive}
                  iconPreviewUrl={iconPreviewUrl}
                  iconUrlInput={iconUrlInput}
                  setIconUrlInput={setIconUrlInput}
                  setIconPreviewUrl={setIconPreviewUrl}
                  iconFile={iconFile}
                  splashFile={splashFile}
                  splashSectionRef={splashSectionRef}
                  onSplashFileChange={handleSplashFileChange}
                  onClearSplash={clearSplashFile}
                  onIconFileChange={handleIconFileChange}
                  onClearIcon={clearIconFile}
                  onSave={() => handleSplashSave(selectedCompany)}
                  onDeactivate={() => handleSplashDeactivate(selectedCompany)}
                  onToggleActive={toggleSplashActive}
                />

                {/* Subscription Details */}
                <SubscriptionDetails
                  theme={theme}
                  permissions={permissions}
                  onChange={handlePermissionChange}
                />

                {/* Company Status */}
                <CompanyStatusToggle
                  theme={theme}
                  permissions={permissions}
                  styles={styles}
                  onToggle={handleToggle}
                />

                {/* Module Permissions */}
                <ModulePermissions
                  permissions={permissions}
                  styles={styles}
                  theme={theme}
                  onToggle={handleToggle}
                />


              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConfigurationManagement;
